// LOTE G1 — tela de parabéns ("Cliente conquistado") para quem marcou ganho.
// Troféu + animação de confete (CSS, sem lib extra). Respeita prefers-reduced-motion.
import React, { useEffect, useMemo } from "react";
import { Trophy } from "lucide-react";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const CORES = ["#f59e0b", "#10b981", "#3b82f6", "#ec4899", "#8b5cf6", "#ef4444", "#14b8a6"];

export default function CelebrationOverlay({ open, onClose, clienteNome, valorFechado, primeiroNome }) {
  const reduceMotion = useMemo(() => {
    try { return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches; }
    catch { return false; }
  }, []);
  const confetes = useMemo(() => {
    if (!open || reduceMotion) return [];
    return Array.from({ length: 60 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 1.2,
      cor: CORES[i % CORES.length],
      size: 6 + Math.random() * 6,
      rot: Math.random() * 360,
    }));
  }, [open, reduceMotion]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="celebration-overlay"
         style={{ background: "radial-gradient(ellipse at center, rgba(16,185,129,.25), rgba(2,6,23,.75))" }}>
      {!reduceMotion && (
        <style>{`
          @keyframes confetti-fall {
            0% { transform: translate3d(0,-20vh,0) rotate(0deg); opacity: 1; }
            100% { transform: translate3d(var(--dx,0),110vh,0) rotate(720deg); opacity: 0.9; }
          }
          @keyframes trofeu-pop {
            0% { transform: scale(0.3) rotate(-30deg); opacity: 0; }
            50% { transform: scale(1.15) rotate(10deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
        `}</style>
      )}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center" role="dialog" aria-modal="true">
        <div className="flex justify-center mb-4" style={!reduceMotion ? { animation: "trofeu-pop .6s ease-out" } : {}}>
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
            <Trophy className="w-12 h-12 text-amber-500" strokeWidth={2.2} />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1" data-testid="celebration-nome">
          Parabéns, {primeiroNome || "campeão"}!
        </h2>
        <div className="text-slate-600 mb-4">
          Cliente conquistado: <b className="text-slate-900" data-testid="celebration-cliente">{clienteNome || "—"}</b>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-6 py-5 mb-5">
          <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Valor fechado</div>
          <div className="text-3xl font-extrabold text-emerald-700 mt-1" data-testid="celebration-valor">{brl(valorFechado)}</div>
        </div>
        <button type="button" onClick={onClose}
                className="w-full py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                data-testid="celebration-continuar">Continuar</button>
      </div>
      {!reduceMotion && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {confetes.map((c) => (
            <span key={c.id}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: `${c.left}%`,
                    width: c.size, height: c.size * 0.4,
                    background: c.cor,
                    borderRadius: 2,
                    transform: `rotate(${c.rot}deg)`,
                    animation: `confetti-fall ${c.duration}s ease-in ${c.delay}s forwards`,
                    "--dx": `${(Math.random() - 0.5) * 200}px`,
                  }} />
          ))}
        </div>
      )}
    </div>
  );
}
